import {
  AnalyzeKeywordsStreamResult,
  AnalyzeKeywordsStreamVars,
  ArticleApiPath,
  KeywordAnalysisProgressEvent
} from '@/common/api/params/article'
import { replacePath } from '@/common/api/rest'
import { LocalStorage, loadStorage } from '@/common/util/storage'

export interface KeywordAnalysisStreamHandlers {
  onProgress: (progress: KeywordAnalysisProgressEvent) => void
  onResult: (result: AnalyzeKeywordsStreamResult) => void
}

/**
 * キーワード分析のSSEストリーミング。
 *
 * 中央fetch層（apiPostFetcher）は response.json() するためストリーミングに使えない。
 * ここでは素の fetch を使うが、認証ヘッダは共通の loadStorage(AUTH_TOKEN) を用いる
 * （featureRewriteStream.ts と同じ方針）。
 */
export const streamAnalyzeKeywords = async (
  vars: AnalyzeKeywordsStreamVars,
  handlers: KeywordAnalysisStreamHandlers,
  signal?: AbortSignal
): Promise<void> => {
  const { path, params } = replacePath(ArticleApiPath.ANALYZE_KEYWORDS_STREAM, vars)

  const response = await fetch(path, {
    method: 'POST',
    signal,
    headers: {
      Authorization: `Bearer ${loadStorage(LocalStorage.AUTH_TOKEN) || ''}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(params)
  })
  if (!response.ok || !response.body) {
    throw response
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  // 旧 ArticleCreator.jsx の event:/data: 行パースを踏襲
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = ''
    let currentEvent = ''
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7).trim()
      } else if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.slice(6))
          if (currentEvent === 'progress') handlers.onProgress(data as KeywordAnalysisProgressEvent)
          else if (currentEvent === 'result') handlers.onResult(data as AnalyzeKeywordsStreamResult)
        } catch {
          // 不完全な行は無視
        }
        currentEvent = ''
      } else if (line !== '') {
        buffer += line + '\n'
      }
    }
  }
}
